/*
 * Citera — global.js
 * Small, dependency-free UI layer: modal drawers (mobile nav + cart) and an
 * AJAX cart backed by Shopify's Cart + Section Rendering APIs. Deferred, no framework.
 */
(function () {
  "use strict";

  var CART_DRAWER_ID = "cart-drawer"; // the <dialog> + the section name requested
  var CART_CONTENT_ID = "cart-drawer-content"; // inner region swapped on every cart change

  /* ---------- Accessibility helpers ---------- */
  var strings = window.CiteraStrings || {};
  // Polite live region for status messages (cart updates, errors).
  var liveRegion;
  function announce(message) {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement("p");
      liveRegion.className = "visually-hidden";
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", "polite");
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = "";
    window.setTimeout(function () {
      liveRegion.textContent = message;
    }, 60);
  }

  /* ---------- Drawers (native <dialog>) ---------- */
  // Remember what opened each dialog so focus returns there on close (WCAG 2.4.3).
  var dialogInvoker = new WeakMap();

  function openDialog(dialog, invoker) {
    if (!dialog || dialog.open) return;
    dialogInvoker.set(dialog, invoker || document.activeElement);
    dialog.showModal();
    document.documentElement.style.overflow = "hidden";
    var focusable = dialog.querySelector(
      "[autofocus], [data-drawer-close], [data-lightbox-close]",
    );
    if (focusable) focusable.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }

  document.addEventListener("click", function (event) {
    var opener = event.target.closest("[data-drawer-open]");
    if (opener) {
      event.preventDefault();
      var target = opener.getAttribute("data-drawer-open");
      openDialog(document.getElementById(target), opener);
      if (target === CART_DRAWER_ID) loadCartUpsell();
      return;
    }
    var closer = event.target.closest("[data-drawer-close]");
    if (closer) {
      event.preventDefault();
      closeDialog(closer.closest("dialog"));
    }
  });

  // Restore scroll + return focus whenever any dialog closes; backdrop click closes.
  document.querySelectorAll("dialog").forEach(function (dialog) {
    dialog.addEventListener("close", function () {
      document.documentElement.style.overflow = "";
      var invoker = dialogInvoker.get(dialog);
      dialogInvoker.delete(dialog);
      if (invoker && typeof invoker.focus === "function" && document.contains(invoker)) {
        invoker.focus();
      }
    });
    dialog.addEventListener("click", function (event) {
      if (event.target === dialog) closeDialog(dialog);
    });
  });

  /* ---------- Predictive search ---------- */
  (function () {
    var input = document.querySelector("[data-predictive-input]");
    var results = document.querySelector("[data-predictive-results]");
    if (!input || !results) return;
    var endpoint =
      input.getAttribute("data-predictive-url") ||
      window.Shopify.routes.root + "search/suggest";
    var timer;
    var controller;

    function render(term) {
      if (term.length < 2) {
        results.innerHTML = "";
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      var url =
        endpoint +
        "?q=" +
        encodeURIComponent(term) +
        "&section_id=predictive-search" +
        "&resources[type]=product,query&resources[limit]=6";
      fetch(url, { signal: controller.signal })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var section = doc.querySelector("#shopify-section-predictive-search");
          results.innerHTML = section ? section.innerHTML : "";
        })
        .catch(function () {});
    }

    input.addEventListener("input", function () {
      var term = input.value.trim();
      clearTimeout(timer);
      timer = setTimeout(function () {
        render(term);
      }, 220);
    });
  })();

  /* ---------- AJAX cart ---------- */
  function refreshCartDrawer(sectionsHTML) {
    var current = document.getElementById(CART_CONTENT_ID);
    var html = sectionsHTML && sectionsHTML[CART_DRAWER_ID];
    if (current && html) {
      var parsed = new DOMParser().parseFromString(html, "text/html");
      var next = parsed.getElementById(CART_CONTENT_ID);
      if (next) current.replaceWith(next); // swap inner region only, keep <dialog> open
    }
    updateCartCount();
    loadCartUpsell();
  }

  // In-cart upsell: fetch complementary recommendations for the first line item.
  function loadCartUpsell() {
    var box = document.querySelector("[data-cart-upsell]");
    if (!box) return;
    var pid = box.getAttribute("data-product-id");
    if (!pid) {
      box.innerHTML = "";
      return;
    }
    fetch(
      window.Shopify.routes.root +
        "recommendations/products?product_id=" +
        pid +
        "&limit=2&intent=related&section_id=cart-upsell",
    )
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var section = doc.querySelector(".shopify-section");
        box.innerHTML = section ? section.innerHTML : "";
      })
      .catch(function () {});
  }

  // Cart note + gift attribute (persist without re-rendering the open drawer).
  function updateCartField(body) {
    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }
  document.addEventListener("change", function (event) {
    var note = event.target.closest("[data-cart-note]");
    if (note) {
      updateCartField({ note: note.value });
      return;
    }
    var gift = event.target.closest("[data-cart-gift]");
    if (gift) {
      updateCartField({ attributes: { Gift: gift.checked ? "Yes" : "" } });
    }
  });

  function updateCartCount() {
    fetch(window.Shopify.routes.root + "cart.js")
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document.querySelectorAll("[data-cart-count]").forEach(function (el) {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      });
  }

  function postCart(endpoint, body) {
    body.sections = [CART_DRAWER_ID];
    body.sections_url = window.location.pathname;
    return fetch(window.Shopify.routes.root + endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  // Inline cart error next to a form + a polite announcement.
  function showCartError(form, message) {
    var text = message || strings.cartError || "Sorry, this item couldn't be added to your cart.";
    var box = form.querySelector("[data-cart-error]");
    if (!box) {
      box = document.createElement("p");
      box.setAttribute("data-cart-error", "");
      box.setAttribute("role", "alert");
      box.className = "cart-error";
      form.appendChild(box);
    }
    box.textContent = text;
    announce(text);
  }
  function clearCartError(form) {
    var box = form.querySelector("[data-cart-error]");
    if (box) box.remove();
  }

  // Intercept add-to-cart forms so the drawer opens instead of a full page load.
  document.addEventListener("submit", function (event) {
    var form = event.target.closest("form[data-cart-form]");
    if (!form) return;
    event.preventDefault();
    var button = form.querySelector('[type="submit"]');
    if (button) button.setAttribute("aria-busy", "true");
    clearCartError(form);

    var formData = new FormData(form);
    var body = {
      items: [{ id: formData.get("id"), quantity: Number(formData.get("quantity") || 1) }],
    };
    postCart("cart/add.js", body)
      .then(function (data) {
        if (data && (data.status || data.description)) {
          showCartError(form, data.description || data.message);
          return;
        }
        refreshCartDrawer(data.sections);
        openDialog(document.getElementById(CART_DRAWER_ID), button);
        announce(strings.cartAdded || "Added to cart");
      })
      .catch(function () {
        showCartError(form);
      })
      .finally(function () {
        if (button) button.removeAttribute("aria-busy");
      });
  });

  // Quantity change + remove inside the drawer (delegated).
  document.addEventListener("change", function (event) {
    var input = event.target.closest("[data-cart-quantity]");
    if (!input) return;
    postCart("cart/change.js", {
      line: Number(input.getAttribute("data-line")),
      quantity: Number(input.value),
    })
      .then(function (data) {
        if (data && data.status) {
          announce(data.description || data.message || strings.cartError);
          return;
        }
        refreshCartDrawer(data.sections);
      })
      .catch(function () {});
  });

  document.addEventListener("click", function (event) {
    var remove = event.target.closest("[data-cart-remove]");
    if (!remove) return;
    event.preventDefault();
    postCart("cart/change.js", {
      line: Number(remove.getAttribute("data-line")),
      quantity: 0,
    })
      .then(function (data) {
        if (data && data.status) return;
        refreshCartDrawer(data.sections);
      })
      .catch(function () {});
  });

  // Stepper +/- buttons: adjust the sibling quantity input, then fire its change.
  document.addEventListener("click", function (event) {
    var step = event.target.closest("[data-qty-step]");
    if (!step) return;
    var input = step.parentElement.querySelector("[data-cart-quantity]");
    if (!input) return;
    var next = Math.max(0, Number(input.value) + Number(step.getAttribute("data-qty-step")));
    input.value = next;
    input.dispatchEvent(new Event("change", { bubbles: true }));
  });

  /* ---------- Product: variant selection + media gallery ---------- */
  function variantData(product) {
    var script = product.querySelector("[data-variant-data]");
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  function updateVariant(product) {
    var controls = product.querySelectorAll("[data-option-index]");
    if (!controls.length) return;
    var chosen = Array.prototype.map.call(controls, function (el) {
      if (el.tagName === "SELECT") return el.value;
      var checked = el.querySelector("input:checked");
      return checked ? checked.value : "";
    });
    var match = variantData(product).find(function (variant) {
      return variant.options.every(function (value, i) {
        return value === chosen[i];
      });
    });

    var idInput = product.querySelector("[data-variant-id]");
    var priceEls = product.querySelectorAll("[data-price]");
    var button = product.querySelector("[data-add-button]");
    var label = product.querySelector("[data-add-label]");

    if (match && idInput) idInput.value = match.id;

    // Swap the gallery to the selected variant's image (variant/swatch gallery).
    if (match && match.featured_media_id) {
      var mediaThumb = product.querySelector('[data-media-id="' + match.featured_media_id + '"]');
      if (mediaThumb) activateThumb(product, mediaThumb);
    }

    priceEls.forEach(function (priceEl) {
      if (match && match.on_sale) {
        priceEl.innerHTML =
          '<span class="product__price-sale">' +
          match.price +
          '</span> <s class="product__price-compare">' +
          match.compare_at_price +
          "</s>";
      } else if (match) {
        priceEl.textContent = match.price;
      }
    });

    // Unit price (e.g. "$0.50/100ml") follows the selected variant.
    var unitPriceEl = product.querySelector("[data-unit-price]");
    if (unitPriceEl && match) {
      if (match.unit_price) {
        var valueEl = unitPriceEl.querySelector("[data-unit-price-value]");
        var baseEl = unitPriceEl.querySelector("[data-unit-price-base]");
        if (valueEl) valueEl.textContent = match.unit_price;
        if (baseEl) baseEl.textContent = (match.unit_price_base || "").replace(/^1\s+/, "");
        unitPriceEl.hidden = false;
      } else {
        unitPriceEl.hidden = true;
      }
    }

    if (button && label) {
      var available = match && match.available;
      button.disabled = !available;
      label.textContent = available ? label.dataset.add : label.dataset.soldout;
    }
  }

  // Country / language selectors submit their localization form on change.
  document.addEventListener("change", function (event) {
    var loc = event.target.closest("[data-localization-select]");
    if (loc && loc.form) {
      loc.form.submit();
      return;
    }
  });

  // Gift-card recipient: reveal the fields + set the "send to recipient" flag.
  document.addEventListener("change", function (event) {
    var toggle = event.target.closest("[data-gift-toggle]");
    if (!toggle) return;
    var wrap = toggle.closest("[data-gift-recipient]");
    if (!wrap) return;
    var fields = wrap.querySelector("[data-gift-fields]");
    var flag = wrap.querySelector("[data-gift-enabled]");
    if (fields) fields.hidden = !toggle.checked;
    if (flag) flag.value = toggle.checked ? "1" : "";
  });

  document.addEventListener("change", function (event) {
    var control = event.target.closest("[data-option-index]");
    if (!control) return;
    var product = control.closest("[data-product]");
    if (!product) return;
    if (control.classList.contains("product__swatches")) {
      control.querySelectorAll(".product__swatch").forEach(function (labelEl) {
        var input = labelEl.querySelector("input");
        labelEl.classList.toggle("is-selected", !!(input && input.checked));
      });
      var checked = control.querySelector("input:checked");
      var out = control.parentElement.querySelector("[data-swatch-selected]");
      if (out && checked) out.textContent = checked.value;
    }
    updateVariant(product);
  });

  function activateThumb(product, thumb) {
    var main = product && product.querySelector("[data-media-main]");
    if (!main || !thumb) return;
    main.src = thumb.getAttribute("data-full");
    main.srcset = thumb.getAttribute("data-srcset");
    var alt = thumb.getAttribute("data-alt");
    if (alt !== null) main.alt = alt;
    var focal = thumb.getAttribute("data-focal");
    main.style.objectPosition = focal ? focal : "";
    product.querySelectorAll("[data-thumb]").forEach(function (t) {
      t.setAttribute("aria-pressed", t === thumb ? "true" : "false");
    });
  }

  document.addEventListener("click", function (event) {
    var thumb = event.target.closest("[data-thumb]");
    if (!thumb) return;
    activateThumb(thumb.closest("[data-product]"), thumb);
  });

  /* ---------- Sticky add-to-cart: show when the main buy button scrolls away, hide over the footer ---------- */
  document.querySelectorAll("[data-product]").forEach(function (product) {
    var sticky = product.querySelector("[data-sticky-atc]");
    var anchor = product.querySelector("[data-add-button]");
    if (!sticky || !anchor || !("IntersectionObserver" in window)) return;
    var footer = document.querySelector(".footer");
    var pastAnchor = false;
    var footerVisible = false;
    function update() {
      sticky.classList.toggle("is-visible", pastAnchor && !footerVisible);
    }
    new IntersectionObserver(
      function (entries) {
        pastAnchor = !entries[0].isIntersecting;
        update();
      },
      { rootMargin: "0px 0px -80px 0px" },
    ).observe(anchor);
    if (footer) {
      new IntersectionObserver(
        function (entries) {
          footerVisible = entries[0].isIntersecting;
          update();
        },
        { rootMargin: "0px 0px 0px 0px" },
      ).observe(footer);
    }
  });

  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-sticky-add]");
    if (!btn) return;
    var product = btn.closest("[data-product]");
    var form = product && product.querySelector("form[data-cart-form]");
    if (!form) return;
    if (form.requestSubmit) form.requestSubmit();
    else form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
  });

  // Buy now: add the item, then go straight to checkout.
  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-buy-now]");
    if (!btn) return;
    var product = btn.closest("[data-product]");
    var form = product && product.querySelector("form[data-cart-form]");
    if (!form) return;
    btn.setAttribute("aria-busy", "true");
    clearCartError(form);
    var fd = new FormData(form);
    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: fd.get("id"), quantity: Number(fd.get("quantity") || 1) }],
      }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        if (data && (data.status || data.description)) {
          btn.removeAttribute("aria-busy");
          showCartError(form, data.description || data.message);
          return;
        }
        window.location.href = window.Shopify.routes.root + "checkout";
      })
      .catch(function () {
        btn.removeAttribute("aria-busy");
        showCartError(form);
      });
  });

  /* ---------- Product recommendations (native Shopify API) ---------- */
  document.querySelectorAll("[data-product-recs]").forEach(function (el) {
    var url = el.getAttribute("data-url");
    if (!url) return;
    fetch(url)
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, "text/html");
        var next = parsed.querySelector("[data-product-recs]");
        if (next) el.replaceWith(next);
      })
      .catch(function () {});
  });

  // Add all (frequently bought together): current product + all recommended single-variant items.
  document.addEventListener("click", function (event) {
    var btn = event.target.closest("[data-add-all]");
    if (!btn) return;
    var scope = btn.closest("[data-product-recs]") || document;
    var ids = [];
    scope.querySelectorAll("form[data-cart-form] [name='id']").forEach(function (input) {
      if (input.value) ids.push(input.value);
    });
    var main = document.querySelector("[data-product] [data-variant-id]");
    if (main && main.value) ids.unshift(main.value);
    if (!ids.length) return;
    btn.setAttribute("aria-busy", "true");
    postCart("cart/add.js", {
      items: ids.map(function (id) {
        return { id: id, quantity: 1 };
      }),
    })
      .then(function (data) {
        refreshCartDrawer(data.sections);
        openDialog(document.getElementById(CART_DRAWER_ID));
      })
      .finally(function () {
        btn.removeAttribute("aria-busy");
      });
  });

  /* ---------- Recently viewed (localStorage, honest history) ---------- */
  (function () {
    var KEY = "citera:recent";
    function readRecent() {
      try {
        return JSON.parse(localStorage.getItem(KEY)) || [];
      } catch (e) {
        return [];
      }
    }
    function esc(s) {
      var d = document.createElement("div");
      d.textContent = s == null ? "" : s;
      return d.innerHTML;
    }
    // Record the current product.
    var itemEl = document.querySelector("[data-recent-item]");
    if (itemEl) {
      try {
        var item = JSON.parse(itemEl.textContent);
        if (item && item.handle) {
          var list = readRecent().filter(function (p) {
            return p.handle !== item.handle;
          });
          list.unshift(item);
          localStorage.setItem(KEY, JSON.stringify(list.slice(0, 8)));
        }
      } catch (e) {}
    }
    // Render into any recently-viewed section.
    document.querySelectorAll("[data-recently-viewed]").forEach(function (sec) {
      var current = sec.getAttribute("data-current");
      var limit = Number(sec.getAttribute("data-limit")) || 4;
      var grid = sec.querySelector("[data-recently-viewed-grid]");
      if (!grid) return;
      var items = readRecent()
        .filter(function (p) {
          return p.handle !== current;
        })
        .slice(0, limit);
      if (!items.length) return;
      grid.innerHTML = items
        .map(function (p) {
          var img = p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">' : "";
          return (
            '<li><div class="product-card"><a href="' +
            esc(p.url) +
            '" class="product-card__media product-card__media--square" tabindex="-1" aria-hidden="true">' +
            img +
            '</a><div class="product-card__info"><h3 class="product-card__title"><a href="' +
            esc(p.url) +
            '">' +
            esc(p.title) +
            '</a></h3><div class="product-card__price">' +
            esc(p.price) +
            "</div></div></div></li>"
          );
        })
        .join("");
      sec.hidden = false;
    });
  })();

  /* ---------- Marquee pause/play (WCAG 2.2.2) ---------- */
  document.addEventListener("click", function (event) {
    var toggle = event.target.closest("[data-marquee-toggle]");
    if (!toggle) return;
    var marquee = toggle.closest(".marquee");
    if (!marquee) return;
    var paused = marquee.classList.toggle("is-paused");
    toggle.setAttribute("aria-pressed", paused ? "true" : "false");
    var label = paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute("aria-label", label);
  });

  /* ---------- Marquee: fill the viewport so the loop never leaves a gap ---------- */
  (function () {
    var tracks = document.querySelectorAll(".marquee__track");
    if (!tracks.length) return;

    function fill(track) {
      // Drop clones from a previous run before remeasuring.
      track.querySelectorAll("[data-marquee-clone]").forEach(function (clone) {
        clone.remove();
      });
      var groups = track.querySelectorAll(".marquee__group");
      if (!groups.length) return;
      var first = groups[0];
      var groupWidth = first.getBoundingClientRect().width;
      var marquee = track.closest(".marquee");
      var viewport = (marquee || track).getBoundingClientRect().width;
      if (!groupWidth || !viewport) return;
      // Enough groups to always cover the viewport plus one to scroll into.
      var needed = Math.ceil(viewport / groupWidth) + 1;
      var count = track.querySelectorAll(".marquee__group").length;
      while (count < needed) {
        var clone = first.cloneNode(true);
        clone.setAttribute("inert", "");
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("data-marquee-clone", "");
        track.appendChild(clone);
        count++;
      }
      // Shift by exactly one group width => a perfect loop at any width.
      track.style.setProperty("--marquee-shift", "-" + groupWidth + "px");
    }

    function run() {
      tracks.forEach(fill);
    }
    run();

    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(run, 200);
    });
  })();

  /* ---------- Countdown timers (real deadline, no evergreen) ---------- */
  function pad(n) {
    return n < 10 ? "0" + n : String(n);
  }

  function tickCountdown(el) {
    var end = new Date(el.getAttribute("data-countdown-end")).getTime();
    var remaining = end - Date.now();
    var units = el.querySelector("[data-countdown-units]");
    var expired = el.querySelector("[data-countdown-expired]");

    if (isNaN(end)) return true; // bad date -> stop, leave placeholders
    if (remaining <= 0) {
      if (units) units.hidden = true;
      if (expired) expired.hidden = false;
      return true; // done
    }

    var secs = Math.floor(remaining / 1000);
    var days = Math.floor(secs / 86400);
    var hours = Math.floor((secs % 86400) / 3600);
    var mins = Math.floor((secs % 3600) / 60);
    var s = secs % 60;
    set(el, "[data-countdown-days]", days);
    set(el, "[data-countdown-hours]", pad(hours));
    set(el, "[data-countdown-minutes]", pad(mins));
    set(el, "[data-countdown-seconds]", pad(s));
    return false;
  }

  function set(root, sel, val) {
    var node = root.querySelector(sel);
    if (node) node.textContent = val;
  }

  var countdowns = Array.prototype.slice.call(document.querySelectorAll("[data-countdown]"));
  if (countdowns.length) {
    var run = function () {
      countdowns = countdowns.filter(function (el) {
        return !tickCountdown(el);
      });
      if (!countdowns.length && window._citeraCountdown) clearInterval(window._citeraCountdown);
    };
    run();
    window._citeraCountdown = setInterval(run, 1000);
  }

  /* ---------- Quick view (Section Rendering API into a modal) ---------- */
  var qvDialog = document.getElementById("quick-view");
  if (qvDialog) {
    var qvContent = qvDialog.querySelector("[data-quick-view-content]");
    document.addEventListener("click", function (event) {
      var btn = event.target.closest("[data-quick-view]");
      if (!btn) return;
      event.preventDefault();
      var handle = btn.getAttribute("data-quick-view");
      qvContent.innerHTML = "";
      openDialog(qvDialog);
      fetch(window.Shopify.routes.root + "products/" + handle + "?section_id=quick-view")
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var inner = doc.querySelector(".qv");
          qvContent.innerHTML = inner ? inner.outerHTML : html;
        })
        .catch(function () {
          closeDialog(qvDialog);
        });
    });
    // Single-variant selector updates the displayed price.
    qvDialog.addEventListener("change", function (event) {
      var sel = event.target.closest("[data-qv-variant]");
      if (!sel) return;
      var opt = sel.options[sel.selectedIndex];
      var priceEl = qvDialog.querySelector("[data-qv-price]");
      if (opt && priceEl && opt.dataset.price) priceEl.textContent = opt.dataset.price;
    });
    // On add-to-cart the global handler opens the cart drawer; close the quick view.
    qvDialog.addEventListener("submit", function () {
      closeDialog(qvDialog);
    });
  }

  /* ---------- Sticky header: fix to top on scroll, with a spacer so content doesn't jump ---------- */
  var stickyHeader = document.querySelector("[data-header-sticky]");
  if (stickyHeader) {
    var headerSpacer = document.createElement("div");
    var headerStuck = false;
    var headerBaseTop = stickyHeader.getBoundingClientRect().top + window.scrollY;
    var updateStuck = function () {
      if (!headerStuck && window.scrollY > headerBaseTop + 4) {
        headerSpacer.style.height = stickyHeader.offsetHeight + "px";
        stickyHeader.parentNode.insertBefore(headerSpacer, stickyHeader);
        stickyHeader.classList.add("is-stuck");
        headerStuck = true;
      } else if (headerStuck && window.scrollY <= headerBaseTop) {
        stickyHeader.classList.remove("is-stuck");
        if (headerSpacer.parentNode) headerSpacer.parentNode.removeChild(headerSpacer);
        headerStuck = false;
      }
    };
    updateStuck();
    window.addEventListener("scroll", updateStuck, { passive: true });
    window.addEventListener(
      "resize",
      function () {
        if (!headerStuck) headerBaseTop = stickyHeader.getBoundingClientRect().top + window.scrollY;
      },
      { passive: true },
    );
  }

  /* ---------- Reveal sections on scroll (respects motion + reduced-motion) ---------- */
  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (
    document.body.classList.contains("anim-on") &&
    !reduceMotion &&
    "IntersectionObserver" in window
  ) {
    var reveals = Array.prototype.slice.call(
      document.querySelectorAll("#main-content > .shopify-section"),
    );
    reveals.shift(); // leave the first (LCP) section visible immediately
    reveals.forEach(function (el) {
      el.classList.add("reveal");
    });
    var revealObserver = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add("reveal--in");
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px" },
    );
    reveals.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------- Faceted filters: submit on change ---------- */
  document.addEventListener("change", function (event) {
    var form = event.target.closest("[data-facets]");
    if (form && (event.target.matches("input") || event.target.matches("select"))) {
      form.submit();
    }
  });

  /* ---------- Faceted filters: only one dropdown open at a time ---------- */
  // The `toggle` event does not bubble, so listen in the capture phase.
  document.addEventListener(
    "toggle",
    function (event) {
      var facet = event.target;
      if (facet.tagName !== "DETAILS" || !facet.classList.contains("facet") || !facet.open) return;
      var bar = facet.closest(".facets__bar");
      if (!bar) return;
      bar.querySelectorAll("details.facet[open]").forEach(function (other) {
        if (other !== facet) other.open = false;
      });
    },
    true,
  );

  // Close any open facet when clicking outside the filter bar.
  document.addEventListener("click", function (event) {
    if (event.target.closest(".facet")) return;
    document.querySelectorAll("details.facet[open]").forEach(function (facet) {
      facet.open = false;
    });
  });

  /* ---------- Customer accounts: recover toggle, address forms, country default ---------- */
  (function () {
    function showRecover(name) {
      document.querySelectorAll("[data-recover-panel]").forEach(function (panel) {
        panel.hidden = panel.getAttribute("data-recover-panel") !== name;
      });
    }
    if (
      window.location.hash === "#recover" &&
      document.querySelector('[data-recover-panel="recover"]')
    ) {
      showRecover("recover");
    }

    document.addEventListener("click", function (event) {
      if (event.target.closest("[data-recover-show]")) return showRecover("recover");
      if (event.target.closest("[data-recover-hide]")) return showRecover("login");

      var newToggle = event.target.closest("[data-address-new-toggle]");
      if (newToggle) {
        var box = document.querySelector("[data-address-new]");
        if (box) box.hidden = !box.hidden;
        return;
      }
      var editToggle = event.target.closest("[data-address-edit-toggle]");
      if (editToggle) {
        var form = document.querySelector(
          '[data-address-edit="' + editToggle.getAttribute("data-address-edit-toggle") + '"]',
        );
        if (form) form.hidden = !form.hidden;
        return;
      }
      var del = event.target.closest("[data-address-delete]");
      if (del && del.dataset.confirm && !window.confirm(del.dataset.confirm)) {
        event.preventDefault();
      }
    });

    // Pre-select country selects from their data-default (no legacy Shopify lib).
    document.querySelectorAll("select[data-default]").forEach(function (select) {
      if (select.dataset.default) select.value = select.dataset.default;
    });
  })();

  /* ---------- Back to top ---------- */
  var toTop = document.querySelector("[data-back-to-top]");
  if (toTop) {
    var onTopScroll = function () {
      toTop.hidden = window.scrollY < 600;
    };
    onTopScroll();
    window.addEventListener("scroll", onTopScroll, { passive: true });
    toTop.addEventListener("click", function () {
      window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    });
  }
})();
